import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { authApi, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Small header action that opens a dialog to change the logged-in user's
 * password. Requires the current password for confirmation. Used by both
 * regular hosts and the admin account — the old fixed "admin123" seed is
 * only applied on first creation, so changes persist across restarts.
 */
export default function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setShowCurrent(false);
    setShowNext(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (next.length < 6) {
      toast.error("A nova senha precisa ter ao menos 6 caracteres.");
      return;
    }
    if (next !== confirm) {
      toast.error("As senhas novas nao conferem.");
      return;
    }
    setSaving(true);
    try {
      await authApi.changePassword(current, next);
      toast.success("Senha alterada com sucesso!");
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-2 border-[#1C1917] rounded-full shadow-[2px_2px_0px_#1C1917]"
          data-testid="change-password-btn"
          title="Alterar senha"
        >
          <KeyRound className="w-4 h-4" strokeWidth={2.5} />
        </Button>
      </DialogTrigger>
      <DialogContent className="border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Alterar senha
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-[11px] font-bold text-[#78716C] uppercase tracking-wider">
              Senha atual
            </Label>
            <div className="relative">
              <Input
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                placeholder="Sua senha atual"
                className="pr-9"
                required
                data-testid="change-password-current-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917]"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-[11px] font-bold text-[#78716C] uppercase tracking-wider">
              Nova senha
            </Label>
            <div className="relative">
              <Input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="Minimo 6 caracteres"
                className="pr-9"
                required
                minLength={6}
                data-testid="change-password-new-input"
              />
              <button
                type="button"
                onClick={() => setShowNext((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917]"
                tabIndex={-1}
              >
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-[11px] font-bold text-[#78716C] uppercase tracking-wider">
              Confirmar nova senha
            </Label>
            <Input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repita a nova senha"
              required
              minLength={6}
              data-testid="change-password-confirm-input"
            />
          </div>

          <Button
            type="submit"
            disabled={saving || !current || !next || !confirm}
            className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all disabled:opacity-60"
            data-testid="change-password-submit-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Alterar senha
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
